import React from 'react';
import { useFetcher } from 'react-router-dom';
import type { ActionResult } from '@oysterjs/core/graphql/actions';
import { Button } from '@oysterjs/ui/button';

import { FeatureDocCallout } from '~/components/feature-doc-callout';
import { FormError } from '~/components/form-error';
import { actionFailureMessage } from '~/components/email-templates/action-failure';
import { GuidelineRowInputs, guidelinesToSave, useGuidelineRows } from './guideline-rows';

const EMAIL_GUIDELINES_PATH = '/app/settings/email';

// The server's maxAgencyEmailGuidelines, which refuses a save with more.
const MAX_AGENCY_EMAIL_GUIDELINES = 10;

const isSameList = (a: string[], b: string[]) =>
  a.length === b.length && a.every((text, index) => text === b[index]);

export const AgencyEmailGuidelinesForm = ({ guidelines }: { guidelines: string[] }) => {
  const fetcher = useFetcher<ActionResult<unknown>>();
  const isSaving = fetcher.state !== 'idle';

  const guidelineRows = useGuidelineRows(guidelines);

  // The rows follow each newly loaded list (another admin's save, or this one's) until the
  // user edits them, so an untouched form never saves an older list over a newer one.
  const [rowsLoadedFrom, setRowsLoadedFrom] = React.useState(guidelines);
  const guidelinesInRows = guidelinesToSave(guidelineRows.rows);
  const hasUnsavedChanges = !isSameList(guidelinesInRows, rowsLoadedFrom);
  const isNewerListLoaded = !isSameList(guidelines, rowsLoadedFrom);
  if (isNewerListLoaded && (!hasUnsavedChanges || isSameList(guidelinesInRows, guidelines))) {
    setRowsLoadedFrom(guidelines);
    guidelineRows.resetRows(guidelines);
  }

  const save = (event: React.FormEvent) => {
    event.preventDefault();
    fetcher.submit(
      { guidelines: guidelinesInRows },
      { method: 'put', encType: 'application/json', action: EMAIL_GUIDELINES_PATH }
    );
  };

  return (
    <fetcher.Form
      onSubmit={save}
      className="space-y-4 rounded-lg bg-white px-4 py-5 shadow sm:px-6 dark:bg-neutral-800"
    >
      <div>
        <h3 className="m-0 p-0 text-lg font-medium text-neutral-900 dark:text-white">
          Guidelines for every email
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Cara follows these in every email it drafts for your agency, with or without a template.
          Guidelines on a template are added on top.
        </p>
      </div>

      {guidelineRows.rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 p-6 text-center dark:border-neutral-600">
          <p className="text-sm font-medium text-neutral-900 dark:text-white">
            No agency guidelines yet
          </p>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Add rules Cara should follow in every email, like how to sign off or what never to say.
          </p>
          <Button color="sky" className="mt-3" onClick={guidelineRows.addRow}>
            Add a guideline
          </Button>
        </div>
      ) : (
        <GuidelineRowInputs
          guidelineRows={guidelineRows}
          canEdit
          maxRows={MAX_AGENCY_EMAIL_GUIDELINES}
        />
      )}

      {(guidelineRows.rows.length > 0 || hasUnsavedChanges) && (
        // pr-4 matches the note box's padding, so Save lines up with Open Memory below it.
        <div className="flex items-center justify-end gap-3 pr-4">
          <FormError message={isSaving ? null : actionFailureMessage(fetcher.data)} />
          <Button type="submit" color="sky" disabled={!hasUnsavedChanges} loading={isSaving}>
            Save guidelines
          </Button>
        </div>
      )}

      <FeatureDocCallout
        title="Cara also remembers things on its own"
        description="Agency memories Cara picked up from conversations live in Settings > Memory and still apply. If a memory and a guideline here disagree, Cara follows the guideline."
        action={
          <Button
            color="white"
            href="/app/settings/memory"
            className="shrink-0 self-end sm:self-start"
          >
            Open Memory
          </Button>
        }
      />
    </fetcher.Form>
  );
};
